import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../../components/ui/toast/ToastContext';

const levelStyles = {
  write: {
    label: 'Editar',
    container: 'border-permissionEditBorder bg-permissionEditBg text-blueDark',
    dot: 'bg-permissionEditDot',
    option: 'border-transparent hover:bg-permissionEditBg',
    selected: 'border-permissionEditBorder bg-permissionEditBg',
    check: 'text-permissionEditDot',
  },
  read: {
    label: 'Visualizar',
    container: 'border-permissionReadBorder bg-permissionReadBg text-blueDark',
    dot: 'bg-permissionReadDot',
    option: 'border-transparent hover:bg-permissionReadBg',
    selected: 'border-permissionReadBorder bg-permissionReadBg',
    check: 'text-permissionReadDot',
  },
};

const levelOptions = ['write', 'read'];

const LevelSelect = ({ value, onChange, label, disabled }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const current = levelStyles[value] || levelStyles.read;

  const closeMenu = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const openMenu = () => {
    if (disabled || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const estimatedMenuHeight = 110;
    const opensUpward = window.innerHeight - rect.bottom < estimatedMenuHeight + 12;
    const desiredTop = opensUpward ? rect.top - estimatedMenuHeight - 8 : rect.bottom + 8;
    setMenuPosition({
      left: Math.max(12, Math.min(rect.left, window.innerWidth - rect.width - 12)),
      top: Math.max(12, Math.min(desiredTop, window.innerHeight - estimatedMenuHeight - 12)),
      width: rect.width,
    });
    setOpen(true);
  };

  const toggleMenu = () => {
    if (open) closeMenu();
    else openMenu();
  };

  useEffect(() => {
    if (!open) return undefined;
    const selectedOption = menuRef.current?.querySelector(`[data-level="${value}"]`);
    selectedOption?.focus();
    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      closeMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu(true);
    };
    const handleViewportChange = () => closeMenu();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, value]);

  const handleOptionKeyDown = (event, optionIndex) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const dir = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (optionIndex + dir + levelOptions.length) % levelOptions.length;
    menuRef.current?.querySelector(`[data-level="${levelOptions[nextIndex]}"]`)?.focus();
  };

  const selectOption = (nextValue) => {
    onChange(nextValue);
    closeMenu(true);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggleMenu}
        onKeyDown={(event) => {
          if (['ArrowDown', 'ArrowUp'].includes(event.key) && !open) {
            event.preventDefault();
            openMenu();
          }
        }}
        style={{ minWidth: '138px' }}
        className={`group relative inline-flex items-center gap-2 rounded-full border px-3 py-2 text-left shadow-sm transition-all duration-200 ${
          current.container
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:-translate-y-px hover:shadow-md'}`}
      >
        <span className={`flex-none rounded-full shadow-sm ${current.dot}`} style={{ width: '12px', height: '12px' }} />
        <span className="flex-1 font-medium" style={{ fontSize: '13px' }}>
          {current.label}
        </span>
        <ChevronDown
          className={`flex-none text-blueSteel transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          size={16}
          strokeWidth={1.8}
        />
      </button>

      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={label}
            className="fixed z-50 rounded-2xl border border-grayExtraLight2 bg-white p-1.5 shadow-xl flex flex-col gap-1.5"
            style={menuPosition}
          >
            {levelOptions.map((optionValue, optionIndex) => {
              const option = levelStyles[optionValue];
              const selected = optionValue === value;

              return (
                <button
                  key={optionValue}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-level={optionValue}
                  onClick={() => selectOption(optionValue)}
                  onKeyDown={(event) => handleOptionKeyDown(event, optionIndex)}
                  className={`flex w-full items-center gap-2.5 rounded-xl border px-3 py-3 text-left text-xs font-medium text-blueDark transition-colors ${
                    selected ? option.selected : option.option
                  }`}
                >
                  <span className={`h-2.5 w-2.5 flex-none rounded-full ${option.dot}`} />
                  <span className="flex-1">{option.label}</span>
                  <span className="flex h-4 w-4 items-center justify-center">
                    {selected && <Check className={option.check} size={14} strokeWidth={2.5} />}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

LevelSelect.propTypes = {
  value: PropTypes.oneOf(['read', 'write']).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

LevelSelect.defaultProps = {
  disabled: false,
};

const CustomCheckbox = ({ checked, indeterminate = false, onChange }) => (
  <div
    onClick={onChange}
    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer ${
      checked || indeterminate
        ? 'bg-tealMedium border-tealMedium'
        : 'bg-white border-grayExtraLight2 hover:border-tealMedium'
    }`}
  >
    {indeterminate && !checked ? (
      <span className="block w-2 h-0.5 bg-white rounded" />
    ) : checked ? (
      <Check size={10} strokeWidth={3} className="text-white" />
    ) : null}
  </div>
);

const AssessorDropdown = ({ assessors, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const current = assessors.find((a) => a.id === selected);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-grayExtraLight2 bg-white px-4 py-2 text-sm text-blueDarker hover:border-tealSoft transition-colors"
      >
        <span className="font-semibold text-blueDarker uppercase tracking-wide text-xs">USUÁRIO:</span>
        <span className="text-blueDarker">{current?.fullName || current?.name || '—'}</span>
        <ChevronDown
          size={15}
          strokeWidth={1.8}
          className={`text-blueSteel transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 z-50 min-w-full overflow-y-auto rounded-2xl border border-grayExtraLight2 bg-white p-1.5 shadow-xl flex flex-col gap-1"
          style={{ maxHeight: '420px' }}
        >
          {assessors.map((assessor) => (
            <button
              key={assessor.id}
              type="button"
              onClick={() => {
                onSelect(assessor.id);
                setOpen(false);
              }}
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-left transition-colors ${
                assessor.id === selected
                  ? 'bg-blueLight font-semibold text-blueDarker'
                  : 'text-blueDarker hover:bg-blueLight'
              }`}
            >
              {assessor.id === selected && (
                <Check size={13} strokeWidth={2.5} className="text-tealMedium flex-shrink-0" />
              )}
              {assessor.id !== selected && <span className="w-[13px]" />}
              {assessor.fullName || assessor.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

AssessorDropdown.propTypes = {
  assessors: PropTypes.array.isRequired,
  selected: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
};

const columns = '40px 1fr 1fr 140px 1fr 150px';

const ClientAccountAccess = ({ onDirtyChange }) => {
  const [admins, setAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [accesses, setAccesses] = useState([]);
  const [originalAccesses, setOriginalAccesses] = useState([]);
  const [direction, setDirection] = useState('ascending');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const loadData = useCallback(
    async (adminId, dir) => {
      setLoading(true);
      try {
        const query = adminId ? `selected_admin_id=${adminId}&direction=${dir}` : `direction=${dir}`;
        const response = await fetchWithCsrf(`/app/master/client_account_accesses?${query}`, {
          headers: { Accept: 'application/json' },
        });
        const payload = camelizeKeys(await response.json());
        if (!response.ok) throw new Error(payload.errors?.join(' ') || 'Erro ao carregar dados.');
        setAdmins(payload.admins || []);
        setSelectedAdminId(payload.selectedAdmin?.id ?? null);
        setAccesses(payload.accesses || []);
        setOriginalAccesses(JSON.parse(JSON.stringify(payload.accesses || [])));
      } catch (error) {
        addToast({ type: 'error', message: error.message });
      } finally {
        setLoading(false);
      }
    },
    [addToast]
  );

  useEffect(() => {
    loadData(selectedAdminId, direction);
  }, [selectedAdminId, direction, loadData]);

  const isDirty = useMemo(() => {
    return (
      JSON.stringify(accesses.map((a) => ({ id: a.id, enabled: a.enabled, level: a.level }))) !==
      JSON.stringify(originalAccesses.map((a) => ({ id: a.id, enabled: a.enabled, level: a.level })))
    );
  }, [accesses, originalAccesses]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const allSelected = accesses.length > 0 && accesses.every((a) => a.enabled);
  const someSelected = accesses.some((a) => a.enabled) && !allSelected;

  const toggleAll = () => {
    const next = !allSelected;
    setAccesses((prev) =>
      prev.map((a) => ({
        ...a,
        enabled: next,
        level: next ? a.level || 'read' : a.level,
      }))
    );
  };

  const toggleOne = (id) => {
    setAccesses((prev) =>
      prev.map((a) => (a.id === id ? { ...a, enabled: !a.enabled, level: a.enabled ? a.level : a.level || 'read' } : a))
    );
  };

  const changeLevel = (id, level) => {
    setAccesses((prev) => prev.map((a) => (a.id === id ? { ...a, level } : a)));
  };

  const handleCancel = () => {
    setAccesses(JSON.parse(JSON.stringify(originalAccesses)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        selected_admin_id: selectedAdminId,
        direction,
        accesses: accesses.filter((a) => a.enabled).map((a) => ({ related_admin_id: a.id, level: a.level })),
      };
      const response = await fetchWithCsrf('/app/master/client_account_accesses', {
        method: 'PUT',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.errors?.join(' ') || 'Erro ao salvar.');
      addToast({ type: 'success', message: 'Acessos atualizados com sucesso.' });
      setOriginalAccesses(JSON.parse(JSON.stringify(accesses)));
    } catch (error) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDirectionChange = (dir) => {
    if (dir !== direction) setDirection(dir);
  };

  return (
    <section className="flex flex-col rounded-2xl bg-white px-6 py-6 shadow-sm gap-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-blueDarker">Acesso a contas de clientes</h2>
          <p className="text-sm text-blueSteel">
            {direction === 'ascending'
              ? 'Selecione os usuários que podem ver as contas vinculadas ao usuário selecionado'
              : 'Selecione os usuários cujas contas o usuário selecionado pode acessar'}
          </p>
        </div>

        <AssessorDropdown assessors={admins} selected={selectedAdminId} onSelect={setSelectedAdminId} />

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleDirectionChange('ascending')}
            className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${
              direction === 'ascending'
                ? 'border-tealMedium bg-tealMedium text-white'
                : 'border-grayExtraLight2 bg-blueLight text-blueDark hover:border-tealSoft'
            }`}
          >
            Ascendente
          </button>
          <button
            type="button"
            onClick={() => handleDirectionChange('descending')}
            className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${
              direction === 'descending'
                ? 'border-tealMedium bg-tealMedium text-white'
                : 'border-grayExtraLight2 bg-blueLight text-blueDark hover:border-tealSoft'
            }`}
          >
            Descendente
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-sm text-blueSteel">Carregando...</div>
      ) : accesses.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-blueSteel">Nenhum assessor encontrado.</div>
      ) : (
        <div className="rounded-xl border border-grayExtraLight2 overflow-hidden">
          <div className="grid items-center bg-blueLight px-5 py-3 gap-4" style={{ gridTemplateColumns: columns }}>
            <CustomCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
            <span className="text-xs font-semibold text-blueSteel uppercase tracking-wide">Nome do Usuário</span>
            <span className="text-xs font-semibold text-blueSteel uppercase tracking-wide">Endereço de E-mail</span>
            <span className="text-xs font-semibold text-blueSteel uppercase tracking-wide">Documento</span>
            <span className="text-xs font-semibold text-blueSteel uppercase tracking-wide">Perfil</span>
            <span className="text-xs font-semibold text-blueSteel uppercase tracking-wide">Permissão</span>
          </div>

          {accesses.map((access) => (
            <div
              key={access.id}
              className="grid h-16 items-center px-5 gap-4 border-t border-grayExtraLight2 transition-colors"
              style={{ gridTemplateColumns: columns }}
            >
              <CustomCheckbox checked={access.enabled} onChange={() => toggleOne(access.id)} />
              <span className={`text-sm font-semibold ${access.enabled ? 'text-blueDarker' : 'text-blueDarker/70'}`}>
                {access.fullName}
              </span>
              <span className="text-sm text-blueSteel truncate">{access.email}</span>
              <span className="text-sm text-blueDarker">{access.cpf}</span>
              <span className="text-sm text-blueDarker">{access.accessProfile?.name || '—'}</span>
              <div className="flex justify-start">
                {access.enabled ? (
                  <LevelSelect
                    value={access.level || 'read'}
                    onChange={(nextLevel) => changeLevel(access.id, nextLevel)}
                    label={`Permissão para ${access.fullName}`}
                  />
                ) : (
                  <span className="text-xs text-blueSteel italic">Sem acesso</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <footer className="mt-2 flex flex-col-reverse justify-end gap-3 sm:flex-row">
        <button
          type="button"
          onClick={handleCancel}
          disabled={saving || !isDirty}
          className="rounded-xl border-b-2 border-r-2 border-graySemiDark bg-blueUltraLight px-7 py-3 text-sm font-semibold text-grayDark transition-colors hover:bg-grayLight disabled:cursor-not-allowed disabled:opacity-50"
        >
          CANCELAR
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="rounded-xl border-b-2 border-r-2 border-tealBase bg-tealMedium px-7 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
        </button>
      </footer>
    </section>
  );
};

ClientAccountAccess.propTypes = {
  onDirtyChange: PropTypes.func,
};

export default ClientAccountAccess;
